import type { PaginationProps } from '@/types/ui';
import { cn } from '../../utils/cn';

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage = 10,
  totalItems,
  className,
}: PaginationProps) => {
  const generatePageNumbers = () => {
    const delta = 2; // Quantas páginas mostrar antes e depois da atual
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const pageNumbers = totalPages > 1 ? generatePageNumbers() : [];

  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems || 0);

  return (
    <nav aria-label="Paginação" className={cn('flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 p-4 bg-card border border-border rounded-lg', className)}>
      {/* Informações dos itens — `totalItems &&` renderizava um "0" solto quando não havia itens */}
      {totalItems ? (
        <div className="text-sm text-muted-foreground">
          Mostrando {startItem} - {endItem} de {totalItems} itens
        </div>
      ) : null}

      {/* Controles de navegação */}
      <div className="flex items-center gap-2">
        {/* Botão anterior */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Página anterior"
          className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            currentPage === 1
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/80'
          }`}
        >
          ← Anterior
        </button>

        {/* Números das páginas */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
              aria-current={page === currentPage ? 'page' : undefined}
              aria-label={typeof page === 'number' ? `Página ${page}` : undefined}
              className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                page === currentPage
                  ? 'bg-primary text-primary-foreground'
                  : page === '...'
                    ? 'text-muted-foreground cursor-default'
                    : 'bg-background text-foreground hover:bg-muted border border-border'
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Botão próximo */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Próxima página"
          className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            currentPage === totalPages
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/80'
          }`}
        >
          Próximo →
        </button>
      </div>
    </nav>
  );
};
